import _ from "lodash";
import moment from "moment";
import Bus from "../bus";
import * as BusEvents from "../constants/bus-events";
import * as Mutations from "./constants/mutations";

/**
 * Safely merges objects with deep merge capabilities
 * Unlike _.extend, this preserves nested object properties
 * @param {Object} target - Target object to merge into
 * @param {Object} source - Source object to merge from
 * @returns {Object} Merged object
 */
function safeObjectMerge(target, source) {
    if (
        !target ||
        !source ||
        typeof target !== "object" ||
        typeof source !== "object"
    ) {
        return target;
    }

    // Use lodash merge for deep merging, but maintain backward compatibility
    // where shallow merge was intentionally used
    return _.merge({}, target, source);
}

/**
 * Safely updates an item in an array by finding and merging
 * @param {Array} array - Array to search in
 * @param {Object} updateItem - Item with updates (must have id)
 * @param {Function} mergeFunction - Function to merge (default: safeObjectMerge)
 * @returns {boolean} True if item was found and updated
 */
function safeArrayUpdate(array, updateItem, mergeFunction = safeObjectMerge) {
    if (
        !Array.isArray(array) ||
        !updateItem ||
        typeof updateItem.id === "undefined"
    ) {
        return false;
    }

    const existing = array.find((item) => item.id === updateItem.id);
    if (existing) {
        // For backward compatibility, we'll still use _.extend behavior
        // but with safer null checks
        _.extend(existing, updateItem);
        return true;
    }
    return false;
}

/**
 * Safely removes an item from array
 * @param {Array} array - Array to remove from
 * @param {Object} removeItem - Item to remove (must have id)
 * @returns {boolean} True if item was found and removed
 */
function safeArrayRemove(array, removeItem) {
    if (
        !Array.isArray(array) ||
        !removeItem ||
        typeof removeItem.id === "undefined"
    ) {
        return false;
    }

    const index = array.findIndex((item) => item.id === removeItem.id);
    if (index !== -1) {
        array.splice(index, 1);
        return true;
    }
    return false;
}

export default {
    /**
     * Layout mutations with improved null safety
     */
    setLayout(state, payload) {
        if (!state || !state.layout) return;

        if (payload && payload.navPos !== undefined)
            state.layout.navPos = payload.navPos;

        if (payload && payload.toolbar !== undefined)
            state.layout.toolbar = payload.toolbar;

        if (payload && payload.footer !== undefined)
            state.layout.footer = payload.footer;

        if (payload && payload.boxed !== undefined)
            state.layout.boxed = payload.boxed;

        if (payload && payload.roundedCorners !== undefined)
            state.layout.roundedCorners = payload.roundedCorners;

        if (payload && payload.viewAnimation !== undefined)
            state.layout.viewAnimation = payload.viewAnimation;
    },

    /**
     * Authentication mutations with null safety
     */
    setLogin(state, payload) {
        if (!state) return;
        state.logged = true;
    },

    setLogout(state, payload) {
        if (!state || !state.layout) return;
        state.layout.navPos = null;
        state.layout.toolbar = null;
        state.logged = false;
    },

    setSplashScreen(state, payload) {
        if (!state) return;
        state.splashScreen = payload;
    },

    /**
     * Corporation mutations with improved error handling
     */
    [Mutations.SET_CORPORATIONS](state, payload) {
        if (!state || !state.corporations || !payload || !payload.data) return;
        state.corporations.data = payload.data;
    },

    [Mutations.NEW_CORPORATION](state, corporation) {
        if (
            !state ||
            !state.corporations ||
            !state.corporations.data ||
            !corporation
        )
            return;
        state.corporations.data.push(corporation);
    },

    [Mutations.UPDATE_CORPORATION](state, corporation) {
        if (
            !state ||
            !state.corporations ||
            !state.corporations.data ||
            !corporation
        )
            return;
        safeArrayUpdate(state.corporations.data, corporation);
    },

    [Mutations.SET_COUPON](state, coupon) {
        if (!state) return;
        state.active_coupon = coupon;
    },

    [Mutations.DELETE_CORPORATION](state, corporation) {
        if (
            !state ||
            !state.corporations ||
            !state.corporations.data ||
            !corporation
        )
            return;

        const existing_corporation = _.find(state.corporations.data, {
            id: corporation.id
        });
        if (existing_corporation && window.Utils && window.Utils.arrayDelete) {
            window.Utils.arrayDelete(
                state.corporations.data,
                existing_corporation
            );
        }
    },

    /**
     * Community mutations with improved error handling
     */
    [Mutations.SET_COMMUNITIES](state, payload) {
        if (!state || !state.communities || !payload || !payload.data) return;
        state.communities.data = payload.data;
    },

    [Mutations.NEW_COMMUNITY](state, community) {
        if (
            !state ||
            !state.communities ||
            !state.communities.data ||
            !community
        )
            return;
        state.communities.data.push(community);
    },

    [Mutations.UPDATE_COMMUNITY](state, community) {
        if (
            !state ||
            !state.communities ||
            !state.communities.data ||
            !community
        )
            return;

        const existing = state.communities.data.find((item) => {
            return item.id === community.id;
        });
        if (existing) {
            _.extend(existing, community);
            // Special handling for locations to maintain backward compatibility
            if (community.locations) {
                existing.locations = community.locations;
            }
        }
    },

    [Mutations.DELETE_COMMUNITY](state, community) {
        if (
            !state ||
            !state.communities ||
            !state.communities.data ||
            !community
        )
            return;

        const existing_community = _.find(state.communities.data, {
            id: community.id
        });
        if (existing_community && window.Utils && window.Utils.arrayDelete) {
            window.Utils.arrayDelete(
                state.communities.data,
                existing_community
            );
        }
    },

    /**
     * User mutations with improved error handling
     */
    [Mutations.SET_USERS](state, payload) {
        if (!state || !state.users || !payload || !payload.data) return;
        state.users.data = payload.data;
    },

    [Mutations.NEW_USER](state, user) {
        if (!state || !state.users || !state.users.data || !user) return;
        state.users.data.push(user);
    },

    [Mutations.UPDATE_USER](state, user) {
        if (!state || !state.users || !state.users.data || !user) return;
        safeArrayUpdate(state.users.data, user);
    },

    [Mutations.REMOVE_USER](state, user) {
        if (!state || !state.users || !state.users.data || !user) return;

        const existing = state.users.data.find((item) => {
            return item.id === user.id;
        });
        if (existing && window.Utils && window.Utils.arrayDelete) {
            window.Utils.arrayDelete(state.users.data, existing);
        }
    },

    /**
     * Order mutations with critical null safety improvements
     */
    [Mutations.SET_ORDERS](state, payload) {
        if (!state || !state.orders || !payload || !payload.data) return;
        state.orders.data = payload.data;
    },

    [Mutations.SET_SIZES](state, payload) {
        if (!state || payload === undefined) return;
        state.sizes = payload;
    },

    [Mutations.SET_DIGITAL_FILES](state, payload) {
        if (!state || payload === undefined) return;
        state.digital_files = payload;
    },

    [Mutations.UPDATE_PENDING_ORDER](state, order) {
        // Critical fix: Add null safety to prevent crashes
        if (
            !state ||
            !state.orders ||
            !state.orders.data ||
            !order ||
            typeof order.id === "undefined"
        ) {
            return;
        }

        const existing = state.orders.data.find((item) => {
            return item.id === order.id;
        });
        if (existing) {
            _.extend(existing, order);
        }
    },

    [Mutations.REMOVE_PENDING_ORDER](state, order) {
        if (
            !state ||
            !state.orders ||
            !state.orders.data ||
            !order ||
            typeof order.id === "undefined"
        ) {
            return;
        }

        const existing = state.orders.data.find((item) => {
            return item.id === order.id;
        });
        if (existing) {
            state.orders.data.splice(state.orders.data.indexOf(existing), 1);
        }
    },

    [Mutations.SET_ORDER_STATUSES](state, payload) {
        if (
            !state ||
            !state.orders ||
            !state.orders.data ||
            !payload ||
            !payload.order
        ) {
            return;
        }

        const { order, statuses } = payload;
        const existing = state.orders.data.find((item) => {
            return item.id === order.id;
        });
        if (existing) {
            existing.statuses = statuses;
        }
    },

    /**
     * Auth user mutations with null safety
     */
    [Mutations.UPDATE_AUTH_USER](state, user) {
        if (!state || !state.user || !user) return;
        _.extend(state.user, user);
    },

    /**
     * Notification mutations with improved handling
     */
    [Mutations.SET_NOTIFICATIONS](state, notifications) {
        if (!state || !state.notifications || !notifications) return;

        if (notifications.data) {
            state.notifications.data = notifications.data;
        }
        if (
            notifications.meta &&
            notifications.meta.pagination &&
            state.notifications.meta
        ) {
            state.notifications.meta.pagination = notifications.meta.pagination;
        }
    },

    [Mutations.MARK_NOTIFICATION_AS_READ](state, notification) {
        if (
            !state ||
            !state.notifications ||
            !state.notifications.data ||
            !notification ||
            typeof notification.id === "undefined"
        ) {
            return;
        }

        const existing = state.notifications.data.find((item) => {
            return item.id === notification.id;
        });
        if (existing) {
            existing.read_at = moment().utc().format("YYYY-MM-DD HH:mm:ss");
        }
    },

    [Mutations.CHANGE_SUSPENSION_STATUS](state, community) {
        if (
            !state ||
            !state.communities ||
            !state.communities.data ||
            !community ||
            typeof community.id === "undefined"
        ) {
            return;
        }

        const existing_community = _.find(state.communities.data, {
            id: community.id
        });
        if (existing_community) {
            community.suspended = !community.suspended;
        }
    },

    /**
     * Cart and state mutations with Bus event handling
     */
    [Mutations.SET_CART](state, cart) {
        if (!state) return;

        state.cart = cart;

        // Safely emit bus event
        if (Bus && Bus.$emit && typeof Bus.$emit === "function") {
            Bus.$emit(BusEvents.CART_UPDATED, cart);
        }
    },

    [Mutations.SET_CART_TAX](state, tax) {
        if (!state) return;
        state.cart_tax = tax;
    },

    [Mutations.SET_LOADING_CART_TAX](state, loading) {
        if (!state) return;
        state.loading_cart_tax = loading;
    },

    [Mutations.SET_CURRENT_CALENDAR_MONTH](state, month) {
        if (
            !state ||
            !state.current_community ||
            !state.current_community.current_month ||
            !month
        ) {
            return;
        }

        _.extend(
            state.current_community.current_month,
            _.pick(month, ["year", "month"])
        );
    },

    [Mutations.SET_CURRENT_COMMUNITY](state, community) {
        if (!state || !community) return;

        // Ensure current_community exists
        if (!state.current_community) {
            state.current_community = {};
        }

        _.extend(state.current_community, community);
    },

    [Mutations.SET_CURRENT_CORPORATION](state, corporation) {
        if (!state || !corporation) return;

        // Ensure current_corporation exists
        if (!state.current_corporation) {
            state.current_corporation = {};
        }

        _.extend(state.current_corporation, corporation);
    },

    [Mutations.SET_DISCOUNTS](state, data) {
        if (!state || !state.discounts || !data || !data.data) return;
        state.discounts.data = data.data;
    },

    [Mutations.SET_PRESET_DATA_FETCHED](state, preset_data_fetched) {
        if (!state) return;
        state.preset_data_fetched = preset_data_fetched;
    },

    [Mutations.SET_CURRENT_ORDER](state, order) {
        if (!state) return;
        state.current_order = order;
    },

    [Mutations.SET_PRICES](state, payload) {
        if (!state || !state.prices || !payload || !payload.data) return;
        state.prices.data = payload.data;
    },

    [Mutations.SET_STATES](state, payload) {
        if (!state || !state.states || !payload || !payload.data) return;
        state.states.data = payload.data;
    },

    [Mutations.AGREED_POLICY](state) {
        if (!state) return;
        state.require_agree_policy = false;
    },

    [Mutations.SET_CSRF](state, csrf) {
        if (!state) return;
        state.csrf = csrf;
    },

    /**
     * Additional mutations with null safety
     */
    SET_HANDLING_CHARGE(state, charge) {
        if (!state) return;
        state.handling_charge = charge;
    },

    SET_LOADING_HANDLING_CHARGE(state, loading) {
        if (!state) return;
        state.loading_handling_charge = loading;
    },

    SET_SHIPPING_IN_PROGRESS(state, value) {
        if (!state || !state.cart) return;
        state.cart.shipping_in_progress = value;
    }
};
